import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Calendar, ClockIcon, Filter, Inbox, Plus } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { parsePlainDateString } from "@/utils/dateTransforms";
import { ScrollArea } from "@/components/ui/scroll-area"

interface Sprint {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  status: 'planned' | 'in_progress' | 'completed' | 'backlog';
  board_id: string;
}

interface SprintSelectorProps {
  selectedSprintId: string | 'all';
  setSelectedSprintId: (sprintId: string | 'all') => void;
  sidebarExpanded: boolean;
  boardId?: string | null;
  onCreateSprint: () => void;
}

export function SprintSelector({ selectedSprintId, setSelectedSprintId, sidebarExpanded, boardId, onCreateSprint }: SprintSelectorProps) {
  const { t } = useTranslation();
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [backlogSprint, setBacklogSprint] = useState<Sprint | null>(null);
  const [regularSprints, setRegularSprints] = useState<Sprint[]>([]);

  // Simplified useEffect - only fetch sprints when boardId changes
  // No real-time subscription needed as it's handled by the parent Board component
  useEffect(() => {
    fetchSprints();
  }, [boardId]);

  const fetchSprints = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('sprints')
        .select('*')
        .order('start_date', { ascending: false });
      
      // If a boardId is provided, filter by that board
      if (boardId) {
        query = query.eq('board_id', boardId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data) {
        // Separate backlog from regular sprints
        const backlog = data.find(sprint => sprint.status === 'backlog') || null;
        const regular = data.filter(sprint => sprint.status !== 'backlog');
        
        setBacklogSprint(backlog);
        setRegularSprints(regular);
        setSprints(data);
        
        // If no sprint is selected, default to the current one or 'all'
        if (selectedSprintId === 'all' && regular.length > 0) {
          const currentSprint = regular.find(sprint => sprint.status === 'in_progress');
          if (currentSprint) {
            setSelectedSprintId(currentSprint.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching sprints:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSprintStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-amber-500';
      case 'in_progress': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'backlog': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getSprintIcon = (status: string, index?: number, totalSprints?: number, isSelected: boolean = false) => {
    if (status === 'backlog') {
      return <Inbox className={`${isSelected ? 'h-5 w-5' : 'h-4 w-4'} text-white`} />;
    }
    if (index !== undefined && totalSprints !== undefined) {
      // Invert the numbering so newest sprint (index 0) gets highest number
      return <span className={`text-white ${isSelected ? 'text-md font-bold' : 'text-xs font-medium'}`}>{totalSprints - index}</span>;
    }
    return <Calendar className={`${isSelected ? 'h-5 w-5' : 'h-4 w-4'} text-white`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <ClockIcon className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col flex-1 overflow-hidden">
      <div className={`${sidebarExpanded ? 'px-2' : 'px-0'} flex items-center justify-between`}>
        <h3 className={`text-sm font-semibold ${sidebarExpanded ? '' : 'text-center'}`}>
          {sidebarExpanded ? t('board.sprints') : ''}
        </h3>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 w-6 p-0" 
          onClick={onCreateSprint}
          title={t('board.addSprint')}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      
      {/* All Sprints button */}
      <Button
        variant="ghost"
        className={`${sidebarExpanded ? 'w-full justify-start' : 'w-full p-2 justify-center'}`}
        onClick={() => setSelectedSprintId('all')}
        title={t('board.allSprints')}
      >
        <div className={`flex items-center ${sidebarExpanded && selectedSprintId === 'all' ? 'w-full' : ''}`}>
          <div className={`bg-gray-500 rounded-md ${selectedSprintId === 'all' ? 'py-2 px-4' : 'p-1 w-6 h-6'} flex items-center ${selectedSprintId === 'all' ? 'justify-start' : 'justify-center'} transition-all duration-200 ${sidebarExpanded && selectedSprintId === 'all' ? 'w-full' : ''}`}>
            <Filter className="h-4 w-4 text-white" />
            {sidebarExpanded && selectedSprintId === 'all' && (
              <span className="ml-2 text-white text-left">{t('board.allSprints')}</span>
            )}
          </div>
          {sidebarExpanded && selectedSprintId !== 'all' && <span className="ml-2 text-left">{t('board.allSprints')}</span>}
        </div>
      </Button>

      {/* Backlog Sprint */}
      {backlogSprint && (
        <Button
          variant="ghost"
          className={`${sidebarExpanded ? 'w-full justify-start' : 'w-full p-2 justify-center'} relative`}
          onClick={() => setSelectedSprintId(backlogSprint.id)}
          title={backlogSprint.title}
        >
          <div className={`flex items-center ${sidebarExpanded && selectedSprintId === backlogSprint.id ? 'w-full' : ''}`}>
            <div className={`${getSprintStatusColor(backlogSprint.status)} rounded-md ${selectedSprintId === backlogSprint.id ? 'py-2 px-4' : 'p-1 w-6 h-6'} flex items-center ${selectedSprintId === backlogSprint.id ? 'justify-start' : 'justify-center'} transition-all duration-200 ${sidebarExpanded && selectedSprintId === backlogSprint.id ? 'w-full' : ''}`}>
              <Inbox className="h-4 w-4 text-white" />
              {sidebarExpanded && selectedSprintId === backlogSprint.id && (
                <span className="ml-2 text-white text-left truncate max-w-[70%]">{backlogSprint.title}</span>
              )}
            </div>
            {sidebarExpanded && selectedSprintId !== backlogSprint.id && (
              <div className="ml-2 text-left flex flex-col">
                <span className="truncate max-w-[120px]">{backlogSprint.title}</span>
              </div>
            )}
          </div>
        </Button>
      )}
        <ScrollArea className="h-[52vh]">
        <div className="space-y-2 mt-2">
          {/* Regular Sprints */}
          {regularSprints.map((sprint, index) => (
            <Button
              key={sprint.id}
              variant="ghost"
              className={`${sidebarExpanded ? 'w-full justify-start' : 'w-full p-2 justify-center'} relative`}
              onClick={() => setSelectedSprintId(sprint.id)}
              title={sprint.title}
            >
              <div className={`flex items-center ${sidebarExpanded && selectedSprintId === sprint.id ? 'w-full' : ''}`}>
                <div className={`${getSprintStatusColor(sprint.status)} rounded-md ${selectedSprintId === sprint.id ? 'py-2 px-4' : 'p-1 w-6 h-6'} flex items-center ${selectedSprintId === sprint.id ? 'justify-start' : 'justify-center'} transition-all duration-200 ${sidebarExpanded && selectedSprintId === sprint.id ? 'w-full' : ''}`}>
                  {getSprintIcon(sprint.status, index, regularSprints.length, selectedSprintId === sprint.id)} 
                  {sidebarExpanded && selectedSprintId === sprint.id && (
                    <div className="ml-2 text-white text-left">
                      <div className="truncate max-w-[70%]">{sprint.title}</div>
                      <div className="text-xs opacity-90">
                        {format(parsePlainDateString(sprint.start_date)!, 'MMM d')} - {format(parsePlainDateString(sprint.end_date)!, 'MMM d')}
                      </div>
                    </div>
                  )}
                </div>
                {sidebarExpanded && selectedSprintId !== sprint.id && (
                  <div className="ml-2 text-left flex flex-col">
                    <span className="truncate max-w-[120px]">{sprint.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(parsePlainDateString(sprint.start_date)!, 'MMM d')} - {format(parsePlainDateString(sprint.end_date)!, 'MMM d')}
                    </span>
                  </div>
                )}
              </div>
              {sprint.status === 'in_progress' && !sidebarExpanded && (
                <Badge variant="secondary" className="absolute -top-1 -right-1 px-1 text-xs">
                  {t('board.current')}
                </Badge>
              )}
            </Button>
          ))}
        </div>
        </ScrollArea>
      
      
      {sprints.length === 0 && (
        <div className={`text-center text-sm text-muted-foreground p-2 ${sidebarExpanded ? '' : 'px-0'}`}>
          {t('board.noSprints')}
        </div>
      )}
    </div>
  );
}
